import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon, XCircleIcon, ArrowTopRightOnSquareIcon, ClockIcon,
  InboxIcon, DocumentIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';
// A date-only column must never become a JS Date — see dateOnly.js.
// `completed_at` below is a TIMESTAMP and stays on new Date(), correctly.
import { formatDateOnly } from '../../lib/dateOnly';
import { DATE_FLOOR, completionMax, expiryMax } from '../../lib/dateBounds';

// Pending Approvals — the cross-staff queue of items that have been
// uploaded but not yet admin-verified. Reads from the unified file
// controller (Phase 1 consolidation) so every action writes to
// file_audit_log and routes through the same verify/reject endpoints
// the Employment File uses. No upload UI here — that lives on each
// person's Employment File.
//
// STEP 0 (build order, 2026-09-11). Which steps need an expiration date is
// read off `onboarding_templates.requires_expiration_date`, never matched on a
// step name.
//
// What was here before was a hardcoded Set of four step names. TWO OF THE FOUR
// MATCHED NO TEMPLATE THAT EXISTS — 'Safe Sleep Practices Training' and
// 'Child Abuse and Neglect Recognition Training' are not the names of any row
// in onboarding_templates (they are 'Safe Sleep Training Module 1' / 'Module 2'
// and 'Child Abuse and Neglect Training'). So those two steps silently skipped
// the expiry prompt for as long as the list existed, and nothing anywhere said
// so. A list of names drifts away from the table it is imitating; the template
// row cannot.
//
// Which provenance values are trusted is now enforced SERVER-SIDE, by migration
// 071's CHECK and the 400 fileController returns ahead of it. The client used to
// keep its own untrusted-value Set to decide whether to ask for a date; it no
// longer decides that, because it now always asks.

// ─── BOTH DATES ARE EDITABLE, ON EVERY ROW ───────────────────────────
//
// Ruled 2026-09-12, with the verification pass blocked on it.
//
// What was here was a `window.prompt` gated on provenance: the director was
// asked for an expiry only when the row carried an UNTRACED one. On the 54 rows
// that already held a traced date he was never asked — and if that date was
// wrong he had no way to say so. Approve wrote a wrong date under his name;
// Send back blamed a staff member for a document that was fine.
//
// So the stored dates are shown, labelled with where they came from, in fields
// he can correct before approving.
//
// THIS DOES NOT REOPEN THE 0.75 RULING. That ruling was about never PRE-FILLING
// an UNTRACED date into an empty field, where it gets confirmed by reflex.
// Showing a date that came off a document, saying so, and letting a director
// correct it is the opposite: it is the only moment the portal ever asks "is
// this right?" about a date it already holds.
//
// The server decides what an edit MEANS — see resolveDateEdit in
// fileController. Editing the expiry makes it `document`; on a
// `computed_from_standard` row, editing the completion date RECOMPUTES the
// expiry and keeps the rule. The client sends what is in the fields and does
// not claim a provenance.

// Show an expiry field when the row has an expiry or the template wants one.
// Provenance is deliberately NOT part of this test — that gate was the defect.
const showsExpiry     = (item) => Boolean(item.requires_expiration_date || item.expiration_date);
const showsCompletion = (item) => Boolean(item.requires_completion_date || item.external_completion_date);

// A `computed_from_standard` row's expiry is not printed on the page — it is
// the completion date plus a named rule, so the two fields are wired together.
const isComputed = (item) => item.expiry_provenance === 'computed_from_standard';

const dateValue = (v) => (v ? String(v).slice(0, 10) : '');

export default function PendingApprovalsPage() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/file/pending-items');
      setPending(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load pending approvals.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const keyFor = (item) => `${item.source}-${item.source_id}`;

  // Per-row edits, keyed the same way the rows are. Seeded from the stored
  // value the first time a field is touched, so an untouched row sends exactly
  // what it already holds and the server records it as unchanged.
  const [edits, setEdits] = useState({});
  const editedValue = (item, field) => {
    const k = keyFor(item);
    if (edits[k] && edits[k][field] !== undefined) return edits[k][field];
    return dateValue(item[field]);
  };
  const setEdit = (item, field, value) => {
    const k = keyFor(item);
    setEdits(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [field]: value } }));
  };

  const approve = async (item) => {
    // Send whatever is in the fields. The client asserts NOTHING about where a
    // date came from — the server compares against what is stored and derives
    // the provenance from what actually changed.
    const body = {};
    if (showsExpiry(item))     body.expiration_date = editedValue(item, 'expiration_date') || null;
    if (showsCompletion(item)) body.external_completion_date = editedValue(item, 'external_completion_date') || null;
    setBusyKey(keyFor(item));
    try {
      await api.post(
        `/file/${item.user_id}/items/${item.source}/${item.source_id}/verify`,
        body
      );
      toast.success(`Verified ${item.title} for ${item.first_name} ${item.last_name}`);
      setEdits(prev => { const next = { ...prev }; delete next[keyFor(item)]; return next; });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Approve failed.');
    } finally { setBusyKey(null); }
  };

  const reject = async (item) => {
    const reason = window.prompt(
      `Send "${item.title}" back to ${item.first_name} ${item.last_name}?\n\nWhat needs to be fixed?`
    );
    if (!reason || !reason.trim()) return;
    setBusyKey(keyFor(item));
    try {
      await api.post(
        `/file/${item.user_id}/items/${item.source}/${item.source_id}/reject`,
        { reason: reason.trim() }
      );
      toast.success('Sent back to staff member.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reject failed.');
    } finally { setBusyKey(null); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  return (
    <div className="animate-slide-up max-w-4xl mx-auto">
      <div className="mb-6 flex items-start gap-4">
        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <InboxIcon className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pending Approvals</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Documents staff have submitted that need your verification.
            {pending.length > 0 && (
              <strong className="text-gray-800"> {pending.length} waiting.</strong>
            )}
          </p>
        </div>
      </div>

      {pending.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <CheckCircleIcon className="w-10 h-10 text-green-400 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">All clear.</p>
          <p className="text-sm text-gray-500 mt-1">Nothing waiting for your approval right now.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map((item) => {
            const busy = busyKey === keyFor(item);
            return (
              <div key={keyFor(item)}
                className="bg-white border border-gray-200 rounded-2xl p-5 border-l-4 border-l-yellow-400">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Link to={`/staff/${item.user_id}`} className="text-sm font-semibold text-gray-900 hover:text-orange-600">
                        {item.first_name} {item.last_name}
                      </Link>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs text-gray-500">{labelForRole(item.user_role)}</span>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs text-gray-500 capitalize">{(item.user_status || '').replace(/_/g, ' ')}</span>
                    </div>
                    <p className="text-base font-medium text-gray-900 flex items-center gap-1.5">
                      <DocumentIcon className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      {item.title}
                      {/* STEP 3 — a REPLACEMENT is a different decision from a
                          first submission, and it has to look like one. The
                          requirement is already satisfied: approving promotes
                          this document and the expiry becomes THIS card's,
                          which can move the compliance date DOWN. Sending it
                          back leaves the person exactly where they are. */}
                      {item.is_replacement && (
                        <span className="text-[11px] font-semibold uppercase tracking-wide
                                         text-blue-700 bg-blue-50 border border-blue-200
                                         rounded px-1.5 py-0.5">
                          Replacement
                        </span>
                      )}
                    </p>
                    {item.is_replacement && (
                      <p className="text-xs text-blue-800 mt-1">
                        Already verified on an earlier document
                        {item.current_expiration_date
                          ? ` expiring ${item.current_expiration_date.slice(0, 10)}`
                          : ''}
                        . Approving makes this one current — the expiry becomes this
                        document's, even if that is sooner. Sending it back changes nothing.
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1.5 flex-wrap">
                      {item.completed_at && (
                        <span className="inline-flex items-center gap-1">
                          <ClockIcon className="w-3.5 h-3.5" />
                          Submitted {new Date(item.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                      {item.external_completion_date && (
                        <span className="inline-flex items-center gap-1">
                          <CalendarDaysIcon className="w-3.5 h-3.5" />
                          Completed {formatDateOnly(item.external_completion_date)}
                        </span>
                      )}
                      {item.expiration_date && (
                        <span className={`inline-flex items-center gap-1 ${
                          TRUSTED_PROVENANCE.has(item.expiry_provenance) ? '' : 'text-amber-700 font-medium'
                        }`}>
                          <CalendarDaysIcon className="w-3.5 h-3.5" />
                          Expires {formatDateOnly(item.expiration_date)}
                          {/* Say where the date came from, every time. A date
                              whose provenance is untrusted must not look the
                              same as one read off a document. */}
                          <span className="text-gray-400">
                            &middot; {PROVENANCE_LABEL[item.expiry_provenance] || PROVENANCE_LABEL.none}
                          </span>
                        </span>
                      )}
                    </div>

                    {/* CORRECT THE DATES BEFORE APPROVING.
                        Shown on every row that has a date or needs one —
                        never gated on provenance, which was the defect. */}
                    {(showsExpiry(item) || showsCompletion(item)) && (
                      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Check these against the document
                        </p>
                        <div className="flex flex-wrap gap-4">
                          {showsCompletion(item) && (
                            <label className="block">
                              <span className="block text-xs text-gray-600 mb-1">Completion date</span>
                              <input
                                type="date" min={DATE_FLOOR} max={completionMax()}
                                value={editedValue(item, 'external_completion_date')}
                                onChange={(e) => setEdit(item, 'external_completion_date', e.target.value)}
                                className="bg-white border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm
                                           focus:outline-none focus:border-orange-500"
                              />
                            </label>
                          )}
                          {showsExpiry(item) && (
                            <label className="block">
                              <span className="block text-xs text-gray-600 mb-1">
                                Expiration date
                                {item.validity_months ? ` · ${item.validity_months} months` : ''}
                              </span>
                              <input
                                type="date" min={DATE_FLOOR} max={expiryMax()}
                                value={editedValue(item, 'expiration_date')}
                                onChange={(e) => setEdit(item, 'expiration_date', e.target.value)}
                                className="bg-white border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm
                                           focus:outline-none focus:border-orange-500"
                              />
                            </label>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
                          {isComputed(item) ? (
                            <>
                              This expiry is <strong>computed</strong> — the completion date plus the
                              rule for this requirement. Change the <strong>completion date</strong> and
                              the expiry recomputes from it. Change the <strong>expiry</strong> directly
                              only if the document prints one, which overrides the computation.
                            </>
                          ) : (
                            <>
                              Currently {PROVENANCE_LABEL[item.expiry_provenance] || PROVENANCE_LABEL.none}.
                              Changing a date records it as read off the document, under your name.
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* ─── THE ENTERED DATE, PRINTED LARGE. 2026-09-14. ───
                        The 2027-09-17 near-miss — three years added instead
                        of two, on two people's CPR — had NO CONTROL BUT THE
                        VERIFIER'S EYE, and it nearly left two coordinators
                        standing in ratio with lapsed cards. The date sat in a
                        small grey chip beside a lot of other small grey text.
                        It is now the largest thing next to the View button,
                        so the verifier reads the typed date and the document
                        in the same glance. Rendered from the YYYY-MM-DD
                        prefix as a string — never `new Date()`, which is the
                        off-by-one display bug fixed on 2026-09-12. */}
                    {item.expiration_date && (
                      <div className="text-right leading-tight px-3 py-1 rounded-lg border border-amber-200 bg-amber-50">
                        <div className="text-[9px] font-bold uppercase tracking-wider text-amber-700">
                          Expiry entered
                        </div>
                        <div className="text-lg font-bold tabular-nums text-amber-900">
                          {String(item.expiration_date).slice(0, 10)}
                        </div>
                      </div>
                    )}
                    {item.document_url && (
                      <a href={item.document_url} target="_blank" rel="noreferrer"
                        className="text-xs font-medium text-gray-700 hover:text-orange-600 border border-gray-200 hover:border-orange-300 rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
                        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /> View
                      </a>
                    )}
                    <button onClick={() => reject(item)} disabled={busy}
                      className="text-xs font-medium text-red-700 hover:bg-red-50 border border-red-200 hover:border-red-300 rounded-lg px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50">
                      <XCircleIcon className="w-3.5 h-3.5" /> Send back
                    </button>
                    <button onClick={() => approve(item)} disabled={busy}
                      className="text-xs font-semibold bg-green-500 hover:bg-green-600 text-white rounded-lg px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50">
                      <CheckCircleIcon className="w-3.5 h-3.5" />
                      {busy ? 'Approving…' : 'Approve'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Plain-English names for the five provenance values. These are shown to a
// director who is about to put their name behind a date, so they say what the
// date IS, not what the enum is called.
const PROVENANCE_LABEL = {
  document:               'read off a document we hold',
  computed_from_standard: 'a training date from a document, plus the state/issuer rule',
  derived_from_template:  'the template\u2019s validity period and nothing else \u2014 nobody has ruled on it',
  transcribed_unverified: 'THE PAPER LOG \u2014 UNVERIFIED, nobody has checked it',
  none:                   'UNKNOWN \u2014 NOT EVIDENCED',
};

// Only these two are safe to inherit without asking.
const TRUSTED_PROVENANCE = new Set(['document', 'computed_from_standard']);

function labelForRole(r) {
  return ({
    director: 'Director', admin: 'Office Admin',
    teacher: 'Teacher', float: 'Float', cook: 'Cook',
  })[r] || r;
}
